"use client";

import { useEffect, useState } from "react";
import { BLACK_FRIDAY_CONFIG } from "@/lib/campaigns/black-friday/config";
import { type CampaignPhase, getCampaignPhase, getCountdownTarget } from "@/lib/campaigns/black-friday/phase";
import type { BlackFridayCopy } from "@/lib/campaigns/black-friday/copy";

interface BlackFridayCountdownProps {
	initialPhase: CampaignPhase;
	statusLabels: BlackFridayCopy["status"];
	labels: BlackFridayCopy["countdown"];
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const statusClasses: Record<CampaignPhase, string> = {
	upcoming: "bg-info text-info-foreground",
	"early-access": "bg-success-soft text-success",
	live: "bg-success-soft text-success",
	ended: "bg-muted text-muted-foreground",
};

function splitRemaining(milliseconds: number) {
	const remaining = Math.max(0, milliseconds);
	return {
		days: Math.floor(remaining / DAY),
		hours: Math.floor((remaining % DAY) / HOUR),
		minutes: Math.floor((remaining % HOUR) / MINUTE),
		seconds: Math.floor((remaining % MINUTE) / SECOND),
	};
}

/**
 * Countdown auf die nächste Kampagnengrenze. Die erste Darstellung entspricht
 * der Server-Phase ohne Zahlen, damit Hydration und Server-Markup identisch
 * bleiben; erst nach dem Mount läuft die Uhr. Nach dem Aktionsende zeigt die
 * Komponente den Endstand statt einer Null-Uhr.
 */
export function BlackFridayCountdown({ initialPhase, statusLabels, labels }: BlackFridayCountdownProps) {
	const [now, setNow] = useState<Date | null>(null);

	useEffect(() => {
		const tick = () => setNow(new Date());
		const kickoff = window.setTimeout(tick, 0);
		const timer = window.setInterval(tick, SECOND);
		return () => {
			window.clearTimeout(kickoff);
			window.clearInterval(timer);
		};
	}, []);

	const phase = now ? getCampaignPhase(now, BLACK_FRIDAY_CONFIG) : initialPhase;
	const target = getCountdownTarget(phase, BLACK_FRIDAY_CONFIG);
	const remaining = target && now ? splitRemaining(target.getTime() - now.getTime()) : null;
	const targetLabel =
		phase === "upcoming"
			? labels.targetUpcoming
			: phase === "early-access"
				? labels.targetEarlyAccess
				: phase === "live"
					? labels.targetLive
					: labels.targetEnded;

	const units = [
		{ value: remaining?.days, label: labels.days },
		{ value: remaining?.hours, label: labels.hours },
		{ value: remaining?.minutes, label: labels.minutes },
		{ value: remaining?.seconds, label: labels.seconds },
	];

	return (
		<div>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
					{targetLabel}
				</p>
				<span
					className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusClasses[phase]}`}
				>
					<span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
					{statusLabels[phase === "early-access" ? "earlyAccess" : phase]}
				</span>
			</div>

			{phase === "ended" ? (
				<p className="mt-3 text-sm leading-relaxed text-muted-foreground">{labels.endedText}</p>
			) : (
				<div className="mt-3 grid grid-cols-4 gap-2" role="timer" aria-live="off">
					{units.map((unit) => (
						<div key={unit.label} className="rounded-lg bg-muted px-2 py-2.5 text-center">
							<span className="block text-xl font-semibold tabular-nums tracking-tight text-foreground">
								{unit.value === undefined ? "–" : String(unit.value).padStart(2, "0")}
							</span>
							<span className="mt-0.5 block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
								{unit.label}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
